import WarningIcon from '@mui/icons-material/Warning';
import { useTheme } from '@mui/material/styles';
import { ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { RobProduct } from '../../store/apiSlices/robs/robsApiParser';
import Box from '@mui/material/Box';
import Button from '../common/Button';
import Card from '../common/Card';
import ClientSideAgGrid from '../common/customAgGrid/ClientSideAgGrid';
import EditableField from '../common/EditableField';
import Typography from '@mui/material/Typography';
import { SectionHeading } from '../common/styled/SectionHeading';
import { useTranslation } from 'react-i18next';

interface ProductsTableProps {
  onSave: (_products: RobProduct[]) => void;
  submitting?: boolean;
  products?: RobProduct[];
  isLoading?: boolean;
  onCancel?: () => void;
  tableColumns?: any;
  setParams?: React.Dispatch<React.SetStateAction<any>>;
  isEditMode?: boolean;
  selectedType?: string;
  onProductChange?: (_productId: number, _field: keyof RobProduct, _value: any) => void;
}

export interface RobsProductTableRef {
  getProducts: () => RobProduct[];
}

interface EditableCellProps {
  field: keyof RobProduct;
  placeholder: string;
  suffix?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  onValueChange?: (_id: number, _field: keyof RobProduct, _value: string) => void;
}

const EditableCellRenderer = React.memo((params: ICellRendererParams & EditableCellProps) => {
  const { data, field, placeholder, suffix, disabled, disabledTooltip, onValueChange } = params;

  const [localValue, setLocalValue] = React.useState(String(data?.[field] || ''));

  const lastCommitted = useRef(String(data?.[field] || ''));
  const debounceTimerRef = useRef<number | null>(null);
  const isUserEditingRef = useRef(false);

  useEffect(() => {
    const newVal = String(data?.[field] || '');
    // Only update localValue if user is not actively editing
    if (!isUserEditingRef.current) {
      setLocalValue(newVal);
      lastCommitted.current = newVal;
    }
  }, [data?.id, data?.[field], data, field]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const regex = /^[0-9]*[.]?[0-9]{0,2}$/;
    if (!regex.test(val)) return;

    isUserEditingRef.current = true;
    setLocalValue(val);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = window.setTimeout(() => {
      onValueChange?.(data.id, field, val);
      lastCommitted.current = val;
    }, 500);
  };

  const handleBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (localValue !== lastCommitted.current) {
      lastCommitted.current = localValue;
      onValueChange?.(data.id, field, localValue); // commit on blur
    }
    // Reset editing flag after a short delay to allow state updates to complete
    setTimeout(() => {
      isUserEditingRef.current = false;
    }, 100);
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <EditableField
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      suffix={suffix}
      disabled={disabled}
      disabledTooltip={disabledTooltip}
      type="number"
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*[.]?[0-9]{0,2}',
      }}
    />
  );
});

EditableCellRenderer.displayName = 'EditableCellRenderer';

const RobsProductTable = forwardRef<RobsProductTableRef, ProductsTableProps>(
  (
    {
      onSave,
      submitting = false,
      products = [],
      isLoading = false,
      onCancel,
      isEditMode = false,
      selectedType = '',
      onProductChange,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const gridRef = useRef<AgGridReact>(null);

    // Keep the authoritative product values without causing React re-renders
    const productRef = useRef<RobProduct[]>(products);

    // Track the product IDs to detect when we have a new set of products
    const productIdsRef = useRef<string>('');

    // Check if it's ACQ type (secondHand or "New vessel acquisition" or "ACQUISITION")
    const isAcqType =
      selectedType?.toLowerCase()?.includes('acq') ||
      selectedType?.toLowerCase()?.includes('acquisition') ||
      selectedType === 'secondHand' ||
      selectedType === 'New vessel acquisition';

    const shouldDisableFields = isEditMode && isAcqType;

    useEffect(() => {
      // Only update productRef if we have a new set of products (different IDs)
      // This prevents overwriting user edits when the container updates localProducts
      const currentProductIds = products
        .map((p) => p.id)
        .sort()
        .join(',');

      if (currentProductIds !== productIdsRef.current) {
        // New set of products - update the ref
        productRef.current = products;
        productIdsRef.current = currentProductIds;
      }
    }, [products]);

    useImperativeHandle(ref, () => ({
      getProducts: () => productRef.current,
    }));

    // Update data directly in grid and our ref to avoid full table refresh
    const handleValueChange = useCallback(
      (productId: number, field: keyof RobProduct, value: string) => {
        // update ref values
        productRef.current = productRef.current.map((p) =>
          p.id === productId ? { ...p, [field]: value } : p,
        );

        // update grid row immutably (data objects may be frozen)
        const rowNode = gridRef.current?.api.getRowNode(productId.toString());
        if (rowNode) {
          const current = rowNode.data || {};
          const newData = { ...current, [field]: value };
          // keep id stable for getRowId
          if ((current as any).id != null && (newData as any).id == null) {
            (newData as any).id = (current as any).id;
          }
          rowNode.setData(newData);
        }

        // Notify parent component of the change for unsaved changes tracking
        onProductChange?.(productId, field, value);
      },
      [onProductChange],
    );

    // Type-in indicator cell renderer (for dedicated column)
    const TypeInIndicatorCellRenderer = useCallback(
      (params: ICellRendererParams) => {
        if (!params.data.isTypeIn) {
          return null;
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <WarningIcon sx={{ color: theme.palette?.warningAmber, fontSize: 20 }} />
            <Typography
              sx={{
                fontSize: '12px',
                color: theme.palette?.warningAmber,
                fontWeight: 500,
              }}
            >
              {t('table.columns.typeIn')}
            </Typography>
          </Box>
        );
      },
      [t, theme],
    );

    const columnDefs = useMemo(() => {
      const cols: any[] = [
        {
          headerName: t('table.columns.productName'),
          field: 'name',
          width: 150,
          maxWidth: 200,
          floatingFilter: false,
        },
        {
          headerName: t('table.columns.category'),
          field: 'category',
          width: 100,
          maxWidth: 150,
          floatingFilter: false,
        },
        {
          headerName: t('table.columns.maker'),
          field: 'maker',
          width: 100,
          maxWidth: 150,
          floatingFilter: false,
        },
        {
          headerName: t('table.columns.type'),
          field: 'type',
          width: 100,
          maxWidth: 120,
          floatingFilter: false,
        },
        {
          headerName: t('table.columns.storageQuantity'),
          field: 'quantity',
          width: 180,
          floatingFilter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="quantity"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.quantityUnit}
              disabled={shouldDisableFields}
              disabledTooltip={shouldDisableFields ? t('robs.acquisitionEditWarning') : ''}
              onValueChange={handleValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
        {
          headerName: t('table.columns.sumpTankQuantity'),
          field: 'netValue',
          width: 180,
          floatingFilter: false,
          cellRenderer: (params: any) => (
            <EditableCellRenderer
              {...params}
              field="netValue"
              placeholder={t('form.quantityPlaceholder')}
              suffix={params?.data?.netValueUnit}
              disabled={shouldDisableFields || !params?.data?.isSumpTankMandatory}
              onValueChange={handleValueChange}
            />
          ),
          suppressKeyboardEvent: () => true,
        },
      ];

      // Conditionally add isTypeIn column if any product has isTypeIn true
      const hasTypeIn = products?.some((p: any) => p.isTypeIn === true);
      if (hasTypeIn) {
        cols.push({
          headerName: '',
          field: 'isTypeIn',
          width: 100,
          floatingFilter: false,
          filter: false,
          sortable: false,
          cellRenderer: TypeInIndicatorCellRenderer,
        });
      }

      return cols;
    }, [handleValueChange, shouldDisableFields, products, TypeInIndicatorCellRenderer, t]);

    return (
      <Box sx={{ width: '100%', marginBottom: '20px' }}>
        <Card sx={{ mt: 2 }}>
          <Box sx={{ mb: 1.5, mt: -1.5 }}>
            <SectionHeading>
              {t('robs.productsAvailableForRob', { count: products.length })}
            </SectionHeading>
          </Box>

          <Box
            sx={{
              height: '2px',
              backgroundColor: theme.palette.dropdown.border,
              mx: '-24px',
              mb: 3,
            }}
          />

          <Box className="ag-theme-alpine">
            <ClientSideAgGrid
              ref={gridRef}
              rowData={products}
              columnDefs={columnDefs}
              loading={isLoading}
              className="robs-product-table-grid"
              themeParams={{
                rowBorder: false,
                headerRowBorder: false,
                headerBackgroundColor: theme.palette.datagridHeaderAlt,
              }}
              infiniteRows
              getRowId={(params: any) => params.data.id?.toString?.()}
              domLayout="autoHeight"
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button variant="red" onClick={onCancel}>
              {t('buttons.cancel')}
            </Button>

            <Button
              variant="outlinedBlue"
              onClick={() => onSave(productRef.current)}
              isLoading={submitting}
            >
              {t('buttons.saveRob')}
            </Button>
          </Box>
        </Card>
      </Box>
    );
  },
);

RobsProductTable.displayName = 'RobsProductTable';

export default RobsProductTable;
